import { colorsPalettes, mixins, rgba } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";
import styled from "styled-components";
import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import { i18nFilter } from "app/filters/i18nFilter";
import { minAbbrNumberFilter, percentageFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import dayjs from "dayjs";
import { getChange } from "app/pages/website-analysis/incoming-traffic/getChange";
import ReactDOMServer from "react-dom/server";

const TableCellStyle = styled.div`
    width: 90px;
    padding: 4px 16px 4px 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    &:nth-child(1) {
        width: 125px;
        justify-content: flex-start;
    }

    &:nth-child(2),
    &:nth-child(3) {
        border-right: 1px solid ${colorsPalettes.carbon[50]};
    }

    &:nth-child(4) {
        width: 120px;
    }

    .ChangeValue--unsigned {
        float: right;
    }
`;

const TableHeadersStyle = styled.div`
    display: flex;
    font-size: 12px;
    font-style: normal;
    line-height: 16px;
    font-weight: 400;
    opacity: 0.8;
`;

const TableRowStyle = styled.div`
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    letter-spacing: 0.6px;
    line-height: 24px;
    display: flex;
`;

const TableCellDomainMarker = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${(p) => p.background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
`;

const TableCellDomainName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TooltipContainer = styled.div`
    padding: 16px;
    border-radius: 6px;
    background-color: ${colorsPalettes.carbon[0]};
    margin: 12px 0 8px 0;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

const Title = styled.span`
    ${mixins.setFont({ $size: 16 })};
    font-weight: bold;
    margin-left: 16px;
`;

const Dash = styled.div`
    background-color: ${colorsPalettes.carbon[100]};
    width: 20px;
    height: 1px;
`;

const ZeroChangeContainer = styled.div`
    display: flex;
    height: 24px;
    align-items: center;
    justify-content: flex-end;
    width: 70px;
`;

export const AbsNumsCompareChangeTooltipFormatter = ({ points }) => {
    const categoryIndex = points[0].point.category;
    const categories = points[0].series.chart.xAxis[0].categories;
    const actualIndex = categories.indexOf(categoryIndex);
    const categoryValue = actualIndex !== -1 ? categories[actualIndex] : null;

    const dateForTitle = dayjs.utc(categoryValue).utc().format("MMM YYYY");
    const headersList = ["Domain", "Traffic", "Share", "Traffic Change"];

    const pointsList = points.map((point) => {
        const currentIndex = point.series.data.findIndex(dataPoint => dataPoint === point.point);
        const prevPointYData = currentIndex > 0 ? point.series.data[currentIndex - 1]?.y : null;

        const change = prevPointYData ? getChange(prevPointYData, point.y) : 0;

        return {
            share: point.point.share,        
            traffic: point.point.y,         
            color: point.series.color,
            name: point.series.name,
            change,
            prevTraffic: prevPointYData || prevPointYData === 0 ? prevPointYData : null,
        };
    });

    return ReactDOMServer.renderToString(
        <TooltipContainer>
            <Title>{dateForTitle}</Title>
            <TableHeaders headers={headersList} />
            <TableRows pointsList={pointsList} />
        </TooltipContainer>,
    );
};

const TableHeaders = ({ headers }) => {
    const headerItems = headers.map((header) => <TableCellStyle key={header}>{header}</TableCellStyle>);
    return <TableHeadersStyle> {headerItems} </TableHeadersStyle>;
};

const TableRows = ({ pointsList }) => {
    return pointsList.map((pointData, index) => (
        <TableRow
            point={pointData}
            key={index}
        />
    ));
};

const TableRow = ({ point }) => {
    const formattedChangeValue = Math.abs(point.change) < 0.0001 ? 0 : point.change;
    let changeValue: string | number =
        formattedChangeValue === 0 ? 0 : yAxisChangeFilter()(Math.abs(formattedChangeValue), 0);

    if (point.prevTraffic === 0) {
        changeValue = point.traffic === 0 ? 0 : i18nFilter()("common.tooltip.change.new");
    }

    return (
        <TableRowStyle>
            <TableCellStyle>
                <TableCellDomainMarker background={point.color} />
                <TableCellDomainName>{point.name}</TableCellDomainName>
            </TableCellStyle>
            <TableCellStyle>{minAbbrNumberFilter()(point.traffic)}</TableCellStyle>
            <TableCellStyle>{percentageFilter()(point.share, 2)}%</TableCellStyle>
            <TableCellStyle>
                {changeValue === 0 ? (
                    <ZeroChangeContainer>
                        <Dash />
                    </ZeroChangeContainer>
                ) : (
                    <ChangeValue
                        descriptionText={""}
                        value={changeValue.toString()}
                        isDecrease={formattedChangeValue < 0}
                        unsigned={point.prevTraffic === 0 && point.traffic !== 0}
                    />
                )}
            </TableCellStyle>
        </TableRowStyle>
    );
};
